/* global document, window */

(() => {
  const DESKTOP_VISIBLE_CARDS = 3;
  const MOBILE_BREAKPOINT = window.matchMedia('(max-width: 989px)');
  const SWIPE_THRESHOLD = 48;

  const setCurrentDot = (dots, activeIndex) => {
    dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  };

  const initializeHero = (root) => {
    if (root.dataset.serviceHeroInitialized === 'true') return;

    const slides = [...root.querySelectorAll('[data-service-hero-slide]')];
    const dots = [...root.querySelectorAll('[data-service-hero-dot]')];
    const previous = root.querySelector('[data-service-hero-previous]');
    const next = root.querySelector('[data-service-hero-next]');

    if (slides.length < 2 || !previous || !next) return;

    root.dataset.serviceHeroInitialized = 'true';
    let activeIndex = 0;

    const showSlide = (requestedIndex) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.hidden = !isActive;
      });
      setCurrentDot(dots, activeIndex);
    };

    previous.addEventListener('click', () => showSlide(activeIndex - 1));
    next.addEventListener('click', () => showSlide(activeIndex + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => showSlide(Number(dot.dataset.serviceHeroDot)));
    });
  };

  const initializeReviews = (root) => {
    if (root.dataset.serviceReviewsInitialized === 'true') return;

    const viewport = root.querySelector('[data-service-reviews-viewport]');
    const track = root.querySelector('[data-service-reviews-track]');
    const cards = [...root.querySelectorAll('[data-service-review-card]')];
    const dots = [...root.querySelectorAll('[data-service-reviews-dot]')];
    const previous = root.querySelector('[data-service-reviews-previous]');
    const next = root.querySelector('[data-service-reviews-next]');

    if (!viewport || !track || cards.length < 2) return;

    root.dataset.serviceReviewsInitialized = 'true';
    const cloneCard = (card) => {
      const clone = card.cloneNode(true);
      clone.removeAttribute('data-service-review-card');
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-service-review-clone', '');
      return clone;
    };
    const cloneCount = Math.min(DESKTOP_VISIBLE_CARDS, cards.length);
    const leadingClones = cards.slice(-cloneCount).map(cloneCard);
    const trailingClones = cards.slice(0, cloneCount).map(cloneCard);
    track.prepend(...leadingClones);
    track.append(...trailingClones);

    let activeIndex = 0;
    let desktopResetPending = false;

    const normalizeIndex = (index) => ((index % cards.length) + cards.length) % cards.length;
    const setDesktopPosition = (physicalIndex, animate) => {
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const offset = (cards[0].getBoundingClientRect().width + gap) * physicalIndex;

      track.classList.toggle('is-jumping', !animate);
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      if (!animate) {
        track.getBoundingClientRect();
        track.classList.remove('is-jumping');
      }
    };

    const resetDesktopPosition = () => {
      desktopResetPending = false;
      setDesktopPosition(cloneCount + activeIndex, false);
    };

    const showReview = (requestedIndex, behavior = 'smooth') => {
      if (!MOBILE_BREAKPOINT.matches && desktopResetPending) resetDesktopPosition();

      const wrappedBeforeStart = requestedIndex < 0;
      const wrappedAfterEnd = requestedIndex >= cards.length;
      activeIndex = normalizeIndex(requestedIndex);

      if (MOBILE_BREAKPOINT.matches) {
        viewport.scrollTo({ left: cards[activeIndex].offsetLeft, behavior });
        track.style.removeProperty('transform');
      } else {
        const physicalIndex = wrappedBeforeStart
          ? cloneCount - 1
          : cloneCount + (wrappedAfterEnd ? cards.length : activeIndex);
        desktopResetPending = wrappedBeforeStart || wrappedAfterEnd;
        setDesktopPosition(physicalIndex, behavior !== 'auto');
      }

      setCurrentDot(dots, activeIndex);
    };

    track.addEventListener('transitionend', (event) => {
      if (event.propertyName !== 'transform' || MOBILE_BREAKPOINT.matches || !desktopResetPending) return;
      resetDesktopPosition();
    });

    previous?.addEventListener('click', () => showReview(activeIndex - 1));
    next?.addEventListener('click', () => showReview(activeIndex + 1));
    dots.forEach((dot) => {
      dot.addEventListener('click', () => showReview(Number(dot.dataset.serviceReviewsDot)));
    });

    let swipeStart = null;

    const finishSwipe = (event, shouldNavigate) => {
      if (!swipeStart || event.pointerId !== swipeStart.pointerId) return;

      const start = swipeStart;
      swipeStart = null;
      viewport.classList.remove('is-swiping');

      if (viewport.hasPointerCapture?.(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }

      if (!shouldNavigate) return;

      const horizontalDistance = event.clientX - start.x;
      const verticalDistance = event.clientY - start.y;
      const isHorizontalSwipe =
        Math.abs(horizontalDistance) >= SWIPE_THRESHOLD && Math.abs(horizontalDistance) > Math.abs(verticalDistance);

      if (!isHorizontalSwipe) return;

      showReview(start.index + (horizontalDistance < 0 ? 1 : -1));
    };

    viewport.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;

      swipeStart = {
        index: activeIndex,
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
      viewport.classList.add('is-swiping');
      viewport.setPointerCapture?.(event.pointerId);
    });
    viewport.addEventListener('pointerup', (event) => finishSwipe(event, true));
    viewport.addEventListener('pointercancel', (event) => finishSwipe(event, false));

    let scrollFrame;
    viewport.addEventListener(
      'scroll',
      () => {
        if (!MOBILE_BREAKPOINT.matches) return;
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          const closestIndex = cards.reduce((closest, card, index) => {
            const currentDistance = Math.abs(card.offsetLeft - viewport.scrollLeft);
            const closestDistance = Math.abs(cards[closest].offsetLeft - viewport.scrollLeft);
            return currentDistance < closestDistance ? index : closest;
          }, 0);
          activeIndex = closestIndex;
          setCurrentDot(dots, activeIndex);
        });
      },
      { passive: true },
    );

    const handleViewportChange = () => showReview(activeIndex, 'auto');
    MOBILE_BREAKPOINT.addEventListener('change', handleViewportChange);
    showReview(0, 'auto');
  };

  const initializeWithin = (container = document) => {
    container.querySelectorAll('[data-idream-service-hero]').forEach(initializeHero);
    container.querySelectorAll('[data-idream-service-reviews]').forEach(initializeReviews);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeWithin());
  } else {
    initializeWithin();
  }

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
